import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaObjectGroup, FaNetworkWired, FaChartBar, FaBalanceScale, FaExchangeAlt, FaCalculator, FaGlobeAfrica, FaFileAlt, FaEye, FaChessKnight, FaLockOpen, FaTools } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #dc3545;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#6f42c1"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#6f42c1"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ConsolidationComptes = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Qu'est-ce que la consolidation des comptes ?",
            answer: "La consolidation des comptes est un processus comptable qui consiste à regrouper les états financiers (bilan, compte de résultat, tableau des flux de trésorerie, etc.) de plusieurs entités juridiquement distinctes (une société mère et ses filiales, par exemple) en un seul ensemble de comptes. Cela donne une vue d'ensemble de la situation financière du groupe, comme s'il s'agissait d'une seule entité économique."
        },
        {
            question: "Pourquoi la consolidation des comptes est-elle importante ?",
            answer: "Elle est importante pour plusieurs raisons : Obligation légale : L'OHADA oblige toute entreprise, qui a son siège ou son activité principale dans l'un des États parties et qui contrôle de manière exclusive ou conjointe une ou plusieurs autres entreprises, ou qui exerce sur elles une influence notable à établir et publier chaque année les états financiers consolidés de l'ensemble constitué par toutes ces entreprises ainsi qu'un rapport sur la gestion de cet ensemble (art. 74, al. 1 AUCE). Cette obligation nait aussi dès le dépassement de certains seuils de chiffre d'affaires, d'effectifs: 500 millions FCFA pour le chiffre d'affaires et effectif de 100 travailleurs dans l'UEMOA selon le règlement d'exécution n°04/99/COM/UEMOA du 19 mai 1999. La consolidation offre également une transparence financière, aide à la prise de décision, facilite la gestion des risques et améliore la communication financière."
        },
        {
            question: "Quels groupes sont concernés par la consolidation des comptes ?",
            answer: "L'OHADA impose à toute entreprise, qui a son siège ou son activité principale dans l'un des États parties et qui contrôle de manière exclusive ou conjointe une ou plusieurs autres entreprises, ou qui exerce sur elles une influence notable à établir et publier chaque année les états financiers consolidés de l'ensemble constitué par toutes ces entreprises ainsi qu'un rapport sur la gestion de cet ensemble (art. 74, al. 1 AUCE). Cette obligation nait aussi dès le dépassement de certains seuils de chiffre d'affaires, d'effectifs: 500 millions FCFA pour le chiffre d'affaires et effectif de 100 travailleurs dans l'UEMOA selon le règlement d'exécution n°04/99/COM/UEMOA du 19 mai 1999."
        },
        {
            question: "Quels services de consolidation proposez-vous ?",
            answer: "CINEF-EC offre une gamme complète de services de consolidation, comprenant : Consolidation statutaire (légale) : Établissement des comptes consolidés conformément aux normes comptables applicables (OHADA, IFRS, etc.). Consolidation de gestion (reporting interne) : Mise en place de tableaux de bord et d'outils de reporting consolidés pour le suivi de la performance du groupe. Assistance à la première consolidation : Accompagnement complet pour les groupes réalisant leur première consolidation (diagnostic, paramétrage des outils, formation des équipes, etc.). Externalisation complète ou partielle de la consolidation : Nous pouvons prendre en charge l'ensemble du processus de consolidation ou vous assister sur des points spécifiques. Audit des comptes consolidés : Certification des comptes consolidés par un commissaire aux comptes. Conseil sur le périmètre de consolidation: détermination des entités à inclure. Conseil sur la méthode de consolidation: intégration globale, intégration proportionnelle, mise en équivalence. Retraitements d'homogénéisation: s'assurer que toutes les entités du groupe appliquent les mêmes méthodes comptables."
        },
        {
            question: "Quels sont les avantages de faire appel à votre cabinet pour la consolidation ?",
            answer: "Les avantages sont nombreux : Expertise : Nos experts possèdent une solide expérience en consolidation et une connaissance approfondie des normes comptables. Fiabilité : Nous garantissons la qualité et la conformité de vos comptes consolidés. Gain de temps : Nous vous déchargeons d'un processus complexe et chronophage. Sécurité : Nous assurons la confidentialité de vos données. Conseil : Nous vous apportons un éclairage précieux sur la performance financière de votre groupe. Maîtrise des coûts: externaliser peut être plus économique que d'embaucher et de former du personnel en interne."
        },
        {
            question: "Pouvez-vous gérer la consolidation de groupes internationaux avec des filiales dans différents pays ?",
            answer: "Oui, notre équipe possède l'expertise nécessaire pour gérer les consolidations internationales, en tenant compte des différentes normes comptables et des problématiques de conversion des devises."
        },
        {
            question: "Pouvez-vous nous aider à mettre en place un processus de consolidation interne ?",
            answer: "Oui, nous proposons des services de conseil et de formation pour vous aider à internaliser tout ou partie du processus de consolidation."
        }
    ];

    const servicesData = [
        {
            icon: <FaObjectGroup />,
            title: "Périmètre de consolidation",
            description: "Identification des entités à inclure dans le périmètre selon les critères de contrôle et détermination des pourcentages de contrôle et d'intérêt.",
            color: "#6f42c1"
        },
        {
            icon: <FaNetworkWired />,
            title: "Méthodes de consolidation",
            description: "Sélection des méthodes appropriées (intégration globale, proportionnelle, mise en équivalence) en fonction des relations entre les entités.",
            color: "#20c997"
        },
        {
            icon: <FaBalanceScale />,
            title: "Homogénéisation comptable",
            description: "Analyse des méthodes comptables appliquées par les différentes entités et retraitements pour assurer la comparabilité des informations.",
            color: "#dc3545"
        },
        {
            icon: <FaExchangeAlt />,
            title: "Élimination des opérations",
            description: "Suppression des transactions et des soldes intra-groupe (ventes, achats, prêts, dividendes, etc.) pour éviter les doubles comptages.",
            color: "#0d6efd"
        },
        {
            icon: <FaCalculator />,
            title: "Traitement des écarts d'acquisition",
            description: "Calcul des écarts d'acquisition (goodwill) lors de la première consolidation, suivi et dépréciation éventuelle.",
            color: "#fd7e14"
        },
        {
            icon: <FaFileAlt />,
            title: "Élaboration des états financiers",
            description: "Production du bilan, du compte de résultat et du tableau de flux de trésorerie consolidés, ainsi que de l'annexe explicative.",
            color: "#198754"
        }
    ];

    const advantagesData = [
        {
            icon: <FaEye />,
            title: "Vision Globale",
            color: "#6f42c1"
        },
        {
            icon: <FaChessKnight />,
            title: "Conformité Légale",
            color: "#dc3545"
        },
        {
            icon: <FaLockOpen />,
            title: "Transparence",
            color: "#0d6efd"
        },
        {
            icon: <FaTools />,
            title: "Expertise Technique",
            color: "#fd7e14"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Accounts Consolidation</h1>
                            <p className="fs-5 mb-4">Expertise technique et accompagnement personnalisé pour vos comptes consolidés</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Consolidation des Comptes
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                La consolidation des comptes est un processus complexe qui requiert une expertise technique pointue et une connaissance approfondie des normes comptables.
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                CINEF-EC vous accompagne à chaque étape de ce processus, de la définition du périmètre de consolidation à la production des états financiers consolidés.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Notre méthodologie pour la consolidation de vos comptes
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaObjectGroup className="fs-1 text-purple" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Analyse de votre groupe</h5>
                                                <p className="text-muted">
                                                    Nous analysons votre structure juridique et organisationnelle pour comprendre vos activités et vos flux intra-groupe.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaNetworkWired className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Expertise technique</h5>
                                                <p className="text-muted">
                                                    Nous maîtrisons les normes comptables applicables (OHADA, IFRS) et les différentes méthodes de consolidation.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaFileAlt className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Production d'états fiables</h5>
                                                <p className="text-muted">
                                                    Nous élaborons des états financiers consolidés fiables et conformes aux exigences réglementaires.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaGlobeAfrica className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Dimension internationale</h5>
                                                <p className="text-muted">
                                                    Nous prenons en compte les spécificités des groupes internationaux (conversion des devises, normes locales, etc.).
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Notre processus de consolidation</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">Une approche méthodique et rigoureuse pour des résultats fiables</p>
                        </div>
                    </div>

                    <div className="row mb-4">
                        {servicesData.slice(0, 3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        {servicesData.slice(3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index + 3}>
                                <ServiceCard delay={`${(index + 3) * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Une vision claire de la performance de votre groupe</h3>
                                <p className="text-white fs-5 mb-4">
                                    La consolidation de vos comptes est essentielle pour une gestion efficace de votre groupe et pour répondre aux exigences légales.
                                    Contactez-nous dès aujourd'hui pour discuter de vos besoins spécifiques en matière de consolidation.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Nous contacter</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Les avantages de la consolidation</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-3" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur la Consolidation</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, #008a44, #dc3545), 
                      url("/assets/images/consolidation-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        .text-purple {
          color: #6f42c1;
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default ConsolidationComptes;